import { Jaco3d } from "../arrangement3d/Jaco3d";
import { LazyPool } from "./LazyPool";
import * as Commons from "oc-commons/Commons";
import { quat, v3 } from "oc-threejs/short3";
import * as Maths3 from "oc-threejs/Maths3";
import * as Scenes from "oc-threejs/Scenes";

import { OBB } from "three/examples/jsm/math/OBB";
import { Box3, Matrix4, Object3D, Quaternion, Vector3 } from "three";

export interface ITransition {
  duration?: number;
  property: string | string[];
  delay?: number;
  timingFunction?: (t: number) => number;
}

/**
 * Jaco ist die Basis-Klasse für alle Arrangement-Objekte
 */
export class Jaco {
  id: number = Commons.createId();

  parent: Jaco | null = null;

  jaco3d: Jaco3d | null = null;

  visible: boolean = true;
  opacity: number | undefined = undefined;

  /**
   * ein Array von deklarativen Animations-Beschreibungen analog zu den Animationen in CSS.
   * duration und delay sind in Millisekunden.
   * property ist z.B. "position", "quaternion", "scale", "opacity" oder "all" oder ein array von property-Namen
   * timing-function ist eine Funktion, die einen Wert zwischen 0 und 1 bekommt und einen Wert zwischen 0 und 1 zurückgibt.
   * Am besten verwendet man die Tween-Easying Funktionen wie TWEEN.Easing.Sinusoidal.InOut (default)
   */
  _transition: ITransition;
  get transition() {
    return this._transition;
  }
  set transition(v) {
    this._transition = v;
  }

  // die Matrizen, wie bei Three. Unterschied: update erfolgt automatisch
  _matrixNeedsUpdate: boolean = true;
  _matrix: Matrix4 = new Matrix4();
  _inverseMatrix: Matrix4 = new Matrix4();

  // normalerweise der Mittelpunkt des Objektes, entspricht der jaco3d-Position, genau wie bei Three
  _position: Vector3 = Vector3.initWithObservation(() => (this._matrixNeedsUpdate = true));

  // die Drehung des Objektes nach der Positionierung - genau wie bei Three
  _quaternion: Quaternion = new Quaternion().onChange(() => (this._matrixNeedsUpdate = true));

  // die Skalierung, insbesondere für Spiegellung verwendet
  _scale: Vector3 = Vector3.initWithObservation(() => (this._matrixNeedsUpdate = true)).set(
    1,
    1,
    1,
  );

  /**
   * seit dem 18.5 ist das neue Verhalten von Jaco3d-Objekten, exakt die Struktur des Jaco-Baums nachzubilden,
   * also im parent.child3dContainer eingehängt zu sein. Das ist in fast allen Anwendungen sinnvoll und
   * behebt Fehler, die durch die Wiederverwendung auftreten, z.B. im ModelGhostPool.
   * Allerdings gibt es Ausnahmen, wenn man also bewußt die Jaco3d in andere parents als den child3dContainer einhängt.
   * Dazu kann man das Flag ensureJaco3dMatchesParent=false am entsprechenden Jaco-Objekt setzen.
   */
  ensureJaco3dMatchesParent: boolean = true;

  // Needed for "get arrangement()" check
  isArrangement?: boolean;

  //------------------------------------------------------------------------------------------------------------------
  // Constructor
  //------------------------------------------------------------------------------------------------------------------

  /**
   * Creates an instance of Jaco.
   * @param {?Jaco} parent
   */
  constructor(parent: Jaco | null) {
    this.parent = parent;
  }

  //------------------------------------------------------------------------------------------------------------------
  // Konstruktion der Jaco-Children und Lazy Erzeugung der Jaco3D-Objekte
  //------------------------------------------------------------------------------------------------------------------

  /**
   * die wichtigste Methode - hier findet die eigentliche Konstruktion statt. 3D-Objekte können hier erstellt
   * werden, aber sie updaten sich selbst, man sollte möglichst hier nicht ihre Werte setzen!
   *
   * Diese Methode muss idempotent implementiert sein!
   *
   * nicht vergessen - super.update3d() aufrufen!
   */
  update3d() {
    // alle Jaco3D - Objekte müssen mittels lazy - Methoden erzeugt werden (dadurch wird die setTouchedByJaco
    // Methode aufgerufen, wodurch ein Abräumen des entsprechenden Jaco3D verhindert wird)
    this.lazyCreateOrTouch3d(() => this.createJaco3d());
  }

  //------------------------------------------------------------------------------------------------------------------
  // Allgemeine Traversierung des ganzen Jaco - Trees
  //------------------------------------------------------------------------------------------------------------------
  /**
   * @callback traverseCallback
   * @param {Jaco} the Element currently traversed
   */

  /**
   * abgeleitete Klassen mit Jaco-Children sollten diese Methode überschreiben und ihre Jaco-Children iterieren
   * @param iterator {traverseCallback}
   */
  traverse(iterator: (jaco: Jaco) => void) {
    iterator(this);
  }

  /**
   * benutzt traverse, um über den Jaco-Baum zu iterieren, und gibt alle Jacos zurück, auf welche predicate zutrifft
   * @param predicate
   * @return {[]}
   */
  filter(predicate: (jaco: Jaco) => boolean): Jaco[] {
    const list: Jaco[] = [];
    this.traverse((x) => {
      if (predicate(x)) list.push(x);
    });
    return list;
  }

  /**
   * iteriert im Baum hoch, bis das Arrangement gefunden wurde (also die Wurzel des Jaco-Baums)
   * @return {?Jaco}
   */
  get arrangement(): Jaco | null {
    let p: Jaco | null = this;
    while (p.parent != null) {
      p = p.parent;
    }
    return p?.isArrangement ? p : null;
  }

  // gibt das assoziierte Metadata-Objekt zurück, wird für SceneExplorer verwendet,
  // um von einem selektierten 3d-Objekt auf z.B. das zugehörende Model zu kommen
  // @return {object}
  getMeta(): any {}

  //------------------------------------------------------------------------------------------------------------------
  // JSON Serialisierung
  //------------------------------------------------------------------------------------------------------------------

  /** */
  toJSON(): Record<string, any> {
    const json: Record<string, any> = {};
    json.position = this.position.toJSON();
    json.quaternion = this.quaternion.toJSON();
    json.scale = this.scale.toJSON();
    json.box = this.getBox().toJSON();
    json.size = this.getSize().toJSON();
    return json;
  }

  /** */
  fromJSON(json: any): this {
    if (json?.position) {
      this.position.fromJSON(json.position);
    }
    if (json?.quaternion) {
      this.quaternion.fromJSON(json.quaternion);
    }
    if (json?.scale) {
      this.scale.fromJSON(json.scale);
    }
    return this;
  }

  //------------------------------------------------------------------------------------------------------------------
  // Jaco3D erzeugen / Tree
  //------------------------------------------------------------------------------------------------------------------
  /** */
  createJaco3d(): Jaco3d {
    return new Jaco3d(this);
  }

  /** */
  getJaco3dName(): string {
    return Commons.typeName(this);
  }

  // Das 3D-Object, in welches Kind-Objekte eingefügt werden. Das kann, muss aber nicht identisch mit
  // this.jaco3d sein, weil man z.B. das Koordinatensystem wechseln möchte - z.B. ein anderer Bezugspunkt, oder eine
  // Rotation
  get child3dContainer(): Object3D | null {
    return this.jaco3d;
  }

  /** */
  lazyCreateOrTouch3d(createCallback: () => Jaco3d): Jaco3d {
    if (this.jaco3d == null) {
      this.jaco3d = createCallback();
    }
    if (this.parent) {
      // siehe Deklaration von ensureJaco3dMatchesParent
      const parentContainer = this.parent.child3dContainer;
      if (
        (parentContainer &&
          this.ensureJaco3dMatchesParent &&
          parentContainer != this.jaco3d.parent) ||
        (!this.ensureJaco3dMatchesParent && this.jaco3d.parent == null)
      ) {
        if (parentContainer) parentContainer.add(this.jaco3d);
      }
    }
    this.jaco3d.setTouchedByJaco();
    return this.jaco3d;
  }

  /** */
  lazyPool<T>(
    array: T[],
    jacoCreator: () => T,
    jaco3DCreator: ((nr: number) => Jaco3d) | null = null,
  ) {
    return new LazyPool<T>(this, array, jacoCreator, jaco3DCreator);
  }

  /** */
  clear3dForDebug() {
    if (this.jaco3d) {
      Scenes.remove(this.jaco3d);
      this.jaco3d = null;
    }
    APP.dirty();
  }

  //------------------------------------------------------------------------------------------------------------------
  // Size & Box
  //------------------------------------------------------------------------------------------------------------------

  /**
   * gibt die BoundingBox im eigenen lokalen Koordinatensystem zurück
   * @returns {THREE.Box3}
   */
  getBox(): Box3 {
    return new Box3().setFromCenterAndSize(v3(), v3());
  }

  /**
   * gibt die Size im eigenen lokalen Koordinatensystem zurück
   * @returns {THREE.Vector3}
   */
  getSize(): Vector3 {
    return this.getBox().getSize(v3());
  }

  /**
   * Wandelt eine Grid-Koordinate in ins lokale Koordinatensystem um.
   * Die Grid-Koordinate ist die Koordinate im Bezugssystem eines Einheitswürfels.
   * Die Eckpunkte eines Einheitsquadrats in 2D haben die Grid-Koordinaten (0,0) (1,0) (1,1) (0,1),
   * entsprechend für 3D hat die linke untere hintere Ecke die Koordinate (0,0,0) und die rechte obere vordere Ecke (1,1,1).
   * (0,0,0) wird dabei auf den this.getBox().min - Vector und (1,1,1) auf den this.getBox().max abgebildet.
   */
  grid(vectorOrX: number | Vector3, y?: number, z?: number) {
    return this.getBox().grid(vectorOrX, y, z);
  }

  /**
   * Gibt die Box3 Box im Weltkoordinatensystem zurück
   * @returns {Box3}
   */
  getBoxWorld(): Box3 {
    return this.getBox().clone().applyMatrix4(this.matrixWorld);
  }

  /**
   * Gibt die OrientedBounding Box im Weltkoordinatensystem zurück
   * @returns {OBB}
   */
  getOBBWorld(): OBB {
    return Maths3.box3AndMatrixToOBB(this.getBox(), this.matrixWorld);
  }

  //------------------------------------------------------------------------------------------------------------------
  // Koordinatensystem - größtenteils wie Object3D plus ein paar Hilfsfunktionen
  // eine Scale fehlt - bisher gab es dafür keinen Anwendungsfall. Sollte eher über Size geregelt sein
  //------------------------------------------------------------------------------------------------------------------

  /**
   * damit man es nicht überschreiben kann
   */
  get position() {
    return this._position;
  }

  /**
   * damit man es nicht überschreiben kann
   */
  get quaternion() {
    return this._quaternion;
  }

  /**
   * damit man es nicht überschreiben kann
   */
  get scale() {
    return this._scale;
  }

  /**
   * @readonly
   * @type {THREE.Matrix4}
   */
  get matrix(): Matrix4 {
    this._maybeUpdateMatrix();
    return this._matrix;
  }

  /**
   * @readonly
   * @type {THREE.Matrix4}
   */
  get inverseMatrix(): Matrix4 {
    this._maybeUpdateMatrix();
    return this._inverseMatrix;
  }

  /**
   * @readonly
   * @type {THREE.Matrix4}
   */
  get matrixWorld(): Matrix4 {
    // cachen ?
    const parents: Jaco[] = [];
    let node: Jaco | null = this;
    do {
      parents.push(node);
      node = node.parent;
    } while (node != null); // node.matrix != null is always true for Jaco
    const m = new Matrix4();
    parents.reverse().forEach((p) => m.multiply(p.matrix));
    return m;
  }

  /**
   * @returns {THREE.Matrix4}
   */
  getMatrixFromMeTo(descendant: Jaco | null): Matrix4 {
    // cachen ?
    if (descendant == null || descendant === this) {
      return new Matrix4();
    }
    if (descendant.parent === this) {
      return descendant.matrix.clone();
    }
    const parents: Jaco[] = [];
    let node: Jaco | null = descendant;
    do {
      parents.push(node);
      if (node === this) {
        const m = new Matrix4();
        parents.reverse().forEach((p) => m.multiply(p.matrix));
        return m;
      }
      if (node) node = node.parent;
    } while (node != null);
    return new Matrix4();
  }

  /**
   * Übernimmt die Matrix komplett (Object3D.applyMatrix multipliziert dagegen).
   * @param {THREE.Matrix4} matrix
   * @returns {Jaco}
   */
  copyMatrix(matrix: Matrix4): this {
    this.matrix.copy(matrix);
    this._decompose();
    return this;
  }

  /**
   * @param {THREE.Matrix4} matrix
   * @returns {Jaco}
   */
  applyMatrix(matrix: Matrix4): this {
    this.matrix.multiplyMatrices(matrix, this.matrix);
    this._decompose();
    return this;
  }

  _decompose() {
    this.matrix.decompose(this.position, this.quaternion, this.scale);
  }

  /** */
  _maybeUpdateMatrix() {
    if (this._matrixNeedsUpdate) {
      this._matrix.compose(this.position, this.quaternion, this.scale);
      this._inverseMatrix.copy(this._matrix).invert();
      this._matrixNeedsUpdate = false;
    }
  }

  /**
   * dummy-Methode, die nur zur API-Kompatibilität mit Object3D existiert
   * @param {?boolean} force
   */
  updateMatrixWorld(force?: boolean) {
    // nichts
  }

  /**
   * @param {THREE.Vector3} axis
   * @param {number} angle
   * @returns {Jaco}
   */
  rotateOnAxis(axis: Vector3, angle: number): this {
    const q1 = new Quaternion();
    q1.setFromAxisAngle(axis, angle);
    this.quaternion.multiply(q1);
    return this;
  }

  /**
   * @param {THREE.Vector3} axis
   * @param {number} distance
   * @returns {Jaco}
   */
  translateOnAxis(axis: Vector3, distance: number): this {
    const v1 = v3();
    v1.copy(axis).applyQuaternion(this.quaternion);
    this.position.add(v1.multiplyScalar(distance));
    return this;
  }

  /**
   * @param {THREE.Vector3} vector
   * @returns {THREE.Vector3}
   */
  localToWorld(vector: Vector3): Vector3 {
    return vector.applyMatrix4(this.matrixWorld);
  }

  /**
   * @param {THREE.Vector3} vector
   * @returns {THREE.Vector3}
   */
  worldToLocal(vector: Vector3): Vector3 {
    const m1 = new Matrix4();
    return vector.applyMatrix4(m1.copy(this.matrixWorld).invert());
  }

  /**
   * @param {*} [result=v3()]
   * @returns {THREE.Vector3}
   */
  getWorldPosition(result: Vector3 = v3()): Vector3 {
    return result.setFromMatrixPosition(this.matrixWorld);
  }

  /**
   * @param {*} [result=quat()]
   * @returns {THREE.Quaternion}
   */
  getWorldQuaternion(result: Quaternion = quat()): Quaternion {
    const position = v3();
    const scale = v3();
    this.matrixWorld.decompose(position, result, scale);
    return result;
  }

  /**
   * @param target
   * @returns {THREE.Vector3}
   */
  getWorldDirection(target: Vector3 = v3()): Vector3 {
    const e = this.matrixWorld.elements;
    return target.set(e[8], e[9], e[10]).normalize();
  }

  /**
   * Gibt eine Drechachsen-Funktion zurück, die als Parameter einen Winkel entgegennimmt, um welcher das Objekt
   * gedreht wird.
   * Die Drehung gilt bei mehrfachem Aufruf absolut zur Ursprungsposition, nicht als jeweiliges Delta.
   * Die Drehachse geht durch pivotPoint (=Drehpunkt) in Richtung axis.
   * @param {THREE.Vector3} pivotPoint Punkt in lokalem Objektspace
   * @param {THREE.Vector3} axis Achse in lokalem Objektspace
   * @return {(number) => void} Drehcallback, welches einen Winkel in Bezug zur ursprünglichen Orientierung erwartet
   */
  createPivotAxis(pivotPoint: Vector3, axis: Vector3): (angle: number) => void {
    const translate0 = new Matrix4().makeTranslation(pivotPoint.x, pivotPoint.y, pivotPoint.z);
    const translate1 = new Matrix4().makeTranslation(-pivotPoint.x, -pivotPoint.y, -pivotPoint.z);
    const rotation = new Matrix4();
    let lastAngle = 0;
    return (angle: number) => {
      this.matrix
        .multiply(translate0)
        .multiply(rotation.makeRotationAxis(axis, angle - lastAngle))
        .multiply(translate1)
        .decompose(this.position, this.quaternion, this.scale);
      lastAngle = angle;
    };
  }

  /**
   * Setzt das Koordinatensystem zurück. Das ist bei Objekten, die wiederverwendet werden und nur per Konstruktion erzeugt werden,
   * sinnvoll und sollte vom eigenen update3d oder dem des Parents aus aufgerufen werden.
   * @returns {Jaco}
   */
  resetCoordinates(): this {
    this.position.set(0, 0, 0);
    this.quaternion.set(0, 0, 0, 1);
    return this;
  }

  copy(o: Jaco) {
    this.position.copy(o.position);
    this.quaternion.copy(o.quaternion);
    this.scale.copy(o.scale);
  }
}
